import { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import { asOrderCheckoutError, createOrder } from "@/hooks/useOrderCheckout";
import type { OrderFormData } from "@/schemas/order.schema";
import { trackCheckoutError } from "@/utils/tracking";

export interface CheckoutParams {
  offerId: number;
  productTitle: string;
  productSubtitle?: string;
  productPrice: string;
  ofertaUrl?: string;
  ofertaProductName?: string;
  accentColor?: string;
  free?: boolean;
}

type CheckoutState = "idle" | "open" | "submitting" | "success" | "error" | "sales-closed";

interface CheckoutContextValue {
  state: CheckoutState;
  params: CheckoutParams | null;
  error: string;
  paymentUrl: string;
  open: (params: CheckoutParams) => void;
  close: () => void;
  submit: (formData: OrderFormData) => Promise<void>;
}

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

export function CheckoutProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CheckoutState>("idle");
  const [params, setParams] = useState<CheckoutParams | null>(null);
  const [error, setError] = useState("");
  const [paymentUrl, setPaymentUrl] = useState("");

  const open = useCallback((p: CheckoutParams) => {
    setParams(p);
    setState("open");
    setError("");
    setPaymentUrl("");
  }, []);

  const close = useCallback(() => {
    if (state === "submitting") return;
    setState("idle");
    setParams(null);
    setError("");
  }, [state]);

  const submit = useCallback(
    async (formData: OrderFormData) => {
      if (!params) return;
      setState("submitting");
      setError("");

      try {
        const url = await createOrder(params.offerId, formData);

        setPaymentUrl(url);
        setState("success");

        if (!params.free) {
          setTimeout(() => {
            window.location.href = url;
          }, 2000);
        }
      } catch (err) {
        const failure = asOrderCheckoutError(err);
        setError(failure.message);
        setState(failure.kind === "sales-closed" ? "sales-closed" : "error");
        trackCheckoutError(params.productTitle, failure.message);
      }
    },
    [params],
  );

  return (
    <CheckoutContext.Provider value={{ state, params, error, paymentUrl, open, close, submit }}>
      {children}
    </CheckoutContext.Provider>
  );
}

CheckoutProvider.displayName = "CheckoutProvider";

export function useCheckout(): CheckoutContextValue {
  const ctx = useContext(CheckoutContext);
  if (!ctx) throw new Error("useCheckout must be used within CheckoutProvider");
  return ctx;
}
